import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { relatorioEstagioService, estagioService } from '@/services/api';
import { PaginationParams } from '@/types';
import { PlusIcon, PencilIcon, TrashIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export const RelatoriosPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();
  const [filters, setFilters] = useState<PaginationParams>({
    page: 1,
    limit: 10,
    search: '',
    tipo: undefined,
    status: undefined,
  });

  // Buscar relatórios
  const { data: relatoriosData, isLoading, error, refetch } = useQuery({
    queryKey: ['relatorios', filters],
    queryFn: () => relatorioEstagioService.list(filters),
    staleTime: 30000, // 30 segundos
  });

  // Buscar estágios para informações relacionadas
  const { data: estagiosData } = useQuery({
    queryKey: ['estagios'],
    queryFn: () => estagioService.list({ page: 1, limit: 1000 }),
    staleTime: 60000, // 1 minuto
  });

  // Mutation para deletar relatório
  const deleteRelatorioMutation = useMutation({
    mutationFn: relatorioEstagioService.delete,
    onSuccess: () => {
      toast.success('Relatório excluído com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['relatorios'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Erro ao excluir relatório';
      toast.error(errorMessage);
    },
  });

  // Handlers
  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDelete = async (id: number, tipo: string) => {
    showConfirm(
      `Excluir ${tipo}`,
      'Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita.',
      async () => {
        await deleteRelatorioMutation.mutateAsync(id);
      },
      'danger'
    );
  };

  const handleFilterChange = (key: keyof PaginationParams, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ page: 1, limit: 10, search: '', tipo: undefined, status: undefined });
  };

  // Funções auxiliares
  const getEstagioInfo = (relatorio: any) => {
    if (relatorio.estagio) {
      return {
        aluno: relatorio.estagio.aluno?.nome || 'Aluno não encontrado',
        empresa: relatorio.estagio.empresa?.nome || 'Empresa não encontrada',
      };
    }

    const estagio = estagiosData?.data.find(e => e.id === relatorio.estagioId);
    return estagio ? {
      aluno: estagio.aluno?.nome || 'Aluno não encontrado',
      empresa: estagio.empresa?.nome || 'Empresa não encontrada',
    } : {
      aluno: 'Estágio não encontrado',
      empresa: 'Estágio não encontrado',
    };
  };

  const getTipoColor = (tipo: string) => {
    const colors = {
      'Inicial': 'bg-blue-100 text-blue-800 border-blue-200',
      'Parcial': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Final': 'bg-green-100 text-green-800 border-green-200',
    };
    return colors[tipo as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusInfo = (dataEntregue: string | null | undefined, prazoEntrega: string) => {
    if (!dataEntregue) {
      const prazo = new Date(prazoEntrega);
      const hoje = new Date();
      if (hoje > prazo) {
        return { text: 'Atrasado', color: 'bg-red-100 text-red-800 border-red-200' };
      }
      return { text: 'Pendente', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
    }
    return { text: 'Entregue', color: 'bg-green-100 text-green-800 border-green-200' };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando relatórios...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Erro ao carregar relatórios</h2>
          <p className="text-gray-600 mb-4">Ocorreu um erro ao buscar os dados.</p>
          <Button onClick={() => refetch()} className="bg-primary-600 hover:bg-primary-700">
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  const relatorios = relatoriosData?.data || [];
  const pagination = relatoriosData?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Relatórios de Estágio</h1>
          <p className="mt-1 text-gray-600">
            Gerencie os relatórios dos estágios cadastrados
          </p>
        </div>
        <Link to="/relatorios/novo">
          <Button asChild className="bg-primary-600 hover:bg-primary-700">
            <PlusIcon className="h-5 w-5 mr-2" />
            Novo Relatório
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{pagination?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Entregues</p>
                <p className="text-2xl font-bold text-gray-900">
                  {relatorios.filter(r => r.dataEntregue).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pendentes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {relatorios.filter(r => !r.dataEntregue).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Páginas</p>
                <p className="text-2xl font-bold text-gray-900">{pagination?.totalPages || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>Filtros</span>
            <span className="text-sm font-normal text-gray-500">
              ({pagination?.total || 0} relatórios encontrados)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar
              </label>
              <input
                type="text"
                placeholder="Aluno, empresa, observação..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo
              </label>
              <select
                value={filters.tipo || ''}
                onChange={(e) => handleFilterChange('tipo', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todos os tipos</option>
                <option value="Inicial">Inicial</option>
                <option value="Parcial">Parcial</option>
                <option value="Final">Final</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todos os status</option>
                <option value="pendente">Pendente</option>
                <option value="entregue">Entregue</option>
                <option value="atrasado">Atrasado</option>
              </select>
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={clearFilters}
                className="w-full"
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Relatórios */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Relatórios</CardTitle>
          <CardDescription>
            {relatorios.length === 0
              ? 'Nenhum relatório encontrado'
              : `${relatorios.length} relatório(s) encontrado(s)`
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {relatorios.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhum relatório encontrado
              </h3>
              <p className="text-gray-500 mb-6">
                {filters.search || filters.tipo || filters.status
                  ? 'Tente ajustar os filtros para encontrar relatórios.'
                  : 'Comece criando um novo relatório de estágio.'
                }
              </p>
              <Link to="/relatorios/novo">
                <Button asChild>
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Novo Relatório
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {relatorios.map((relatorio) => {
                const estagioInfo = getEstagioInfo(relatorio);
                const statusInfo = getStatusInfo(relatorio.dataEntregue, relatorio.prazoEntrega);

                return (
                  <div
                    key={relatorio.id}
                    className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-200 bg-white"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      {/* Informações principais */}
                      <div className="flex-1 space-y-4">
                        {/* Header do relatório */}
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="flex items-center gap-2">
                            <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                            <h3 className="text-lg font-semibold text-gray-900">
                              Relatório #{relatorio.id}
                            </h3>
                          </div>

                          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getTipoColor(relatorio.tipo)}`}>
                            {relatorio.tipo}
                          </span>

                          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusInfo.color}`}>
                            {statusInfo.text}
                          </span>
                        </div>

                        {/* Informações do estágio */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div>
                            <p className="text-sm text-gray-500">Estágio</p>
                            <p className="font-medium text-gray-900">#{relatorio.estagioId}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Prazo de Entrega</p>
                            <p className="font-medium text-gray-900">
                              {formatDate(relatorio.prazoEntrega)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Data Entregue</p>
                            <p className="font-medium text-gray-900">
                              {relatorio.dataEntregue
                                ? formatDate(relatorio.dataEntregue)
                                : 'Não entregue'
                              }
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Observações</p>
                            <p className="font-medium text-gray-900 truncate">
                              {relatorio.observacao || 'Nenhuma'}
                            </p>
                          </div>
                        </div>

                        {/* Informações do aluno e empresa */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500">Aluno</p>
                            <p className="font-medium text-gray-900">{estagioInfo.aluno}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Empresa</p>
                            <p className="font-medium text-gray-900">{estagioInfo.empresa}</p>
                          </div>
                        </div>
                      </div>

                      {/* Ações */}
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                          className="flex items-center gap-2"
                        >
                          <Link to={`/relatorios/${relatorio.id}/editar`}>
                            <PencilIcon className="h-4 w-4 " />
                            Editar
                          </Link>
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(relatorio.id, relatorio.tipo)}
                          disabled={deleteRelatorioMutation.isPending}
                          className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:border-red-300"
                        >
                          <TrashIcon className="h-4 w-4" />
                          Excluir
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Paginação */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex justify-center mt-8">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) - 1)}
                  disabled={(filters.page || 1) <= 1}
                >
                  Anterior
                </Button>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <Button
                        key={page}
                        variant={(filters.page || 1) === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className="w-10"
                      >
                        {page}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) + 1)}
                  disabled={(filters.page || 1) >= pagination.totalPages}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteRelatorioMutation.isPending}
      />
    </div>
  );
};